/**
 * Abrir o popup do mapa por código (ex.: item da lista "Últimos focos"), pelo
 * mesmo gerenciador de cliques (`PopupHost`), que se registra aqui.
 */
import type { ReactNode } from 'react';

export interface OpenPopupOptions {
  /** Anel pulsante no ponto (destaque do foco escolhido na lista). */
  marker?: boolean;
  /** O popup pode ser arrastado para longe do ponto (fica ligado a ele por uma linha). */
  movel?: boolean;
  /**
   * Área que a ficha não deve cobrir (extensão `[minx, miny, maxx, maxy]` na
   * projeção do mapa, ou null se ela não existe agora). É consultada de novo
   * quando o mapa muda: a área pode andar (o sistema de nuvens no player). Só
   * no desktop, e só para quem a declara — ver `popupArea.colocarFora`.
   */
  evitar?: AreaAEvitar;
  /** Abre abaixo do ponto, presa pelo topo (a ficha que muda de altura não sobe: ver PopupHost). */
  abaixo?: boolean;
}

export type AreaAEvitar = () => readonly number[] | null;

type Opener = (coordinate: [number, number], content: ReactNode, opts?: OpenPopupOptions) => void;

let opener: Opener | null = null;

/** (PopupHost) Registra quem abre o popup; devolve a função que desfaz o registro. */
export function registerPopupOpener(fn: Opener): () => void {
  opener = fn;
  return () => {
    if (opener === fn) opener = null;
  };
}

let closer: (() => void) | null = null;

/** (PopupHost) Registra quem fecha o popup; devolve a função que desfaz o registro. */
export function registerPopupCloser(fn: () => void): () => void {
  closer = fn;
  return () => {
    if (closer === fn) closer = null;
  };
}

/** Fecha o popup aberto (ex.: a ficha da previsão quando o modo "Próximos dias" sai). */
export function closePopup(): void {
  closer?.();
}

/** Abre o popup na coordenada do mapa. Falso se o mapa ainda não existe. */
export function openPopupAt(coordinate: [number, number], content: ReactNode, opts?: OpenPopupOptions): boolean {
  if (!opener) return false;
  opener(coordinate, content, opts);
  return true;
}

/**
 * Popup por COORDENADA, para camadas sem features (ex.: o radar, que é
 * imagem): consultado quando nenhuma feature respondeu ao clique, antes do
 * município de reserva. Devolve o conteúdo ou null (o próximo é consultado).
 */
export type CoordinatePopup = (coordinate: [number, number]) => ReactNode | typeof CONSUMIDO | null | Promise<ReactNode | typeof CONSUMIDO | null>;

/**
 * Resposta de um popup por coordenada que tratou o clique por conta própria
 * (a ficha do ponto do "Próximos dias" abre no cartão da direita, não num
 * balão): o PopupHost não abre nada — nem o município de reserva.
 */
export const CONSUMIDO = Symbol('clique consumido');

export interface CoordinatePopupOptions {
  /** Maior primeiro; empate = ordem de registro (padrão 0, o comportamento de antes). */
  prioridade?: number;
  /** Responde também no modo exclusivo (o "Próximos dias"), quando as camadas observadas estão ocultas. */
  noModoExclusivo?: boolean;
}

interface Registro {
  fn: CoordinatePopup;
  prioridade: number;
  noModoExclusivo: boolean;
  ordem: number;
}

const coordinatePopups: Registro[] = [];
let ordem = 0;

export function registerCoordinatePopup(fn: CoordinatePopup, opts: CoordinatePopupOptions = {}): () => void {
  // A mesma função registrada de novo substitui a anterior (como o `Set` de antes).
  const antiga = coordinatePopups.findIndex((x) => x.fn === fn);
  if (antiga >= 0) coordinatePopups.splice(antiga, 1);
  const r: Registro = { fn, prioridade: opts.prioridade ?? 0, noModoExclusivo: !!opts.noModoExclusivo, ordem: ordem++ };
  coordinatePopups.push(r);
  coordinatePopups.sort((a, b) => b.prioridade - a.prioridade || a.ordem - b.ordem);
  return () => {
    const i = coordinatePopups.indexOf(r);
    if (i >= 0) coordinatePopups.splice(i, 1);
  };
}

/**
 * Modo exclusivo: enquanto `fn` disser sim, só respondem os popups marcados
 * `noModoExclusivo` (as camadas dos outros estão ocultas, e a ficha de uma
 * camada escondida não pode abrir). Registrado pelo "Próximos dias".
 */
let modoExclusivo: () => boolean = () => false;

export function registerModoExclusivo(fn: () => boolean): () => void {
  modoExclusivo = fn;
  return () => {
    if (modoExclusivo === fn) modoExclusivo = () => false;
  };
}

/** (PopupHost) Primeiro conteúdo não nulo dos popups por coordenada. */
export async function coordinatePopupAt(coordinate: [number, number]): Promise<ReactNode | typeof CONSUMIDO | null> {
  const exclusivo = modoExclusivo();
  for (const { fn, noModoExclusivo } of [...coordinatePopups]) {
    if (exclusivo && !noModoExclusivo) continue;
    try {
      const node = await fn(coordinate);
      if (node !== null && node !== undefined && node !== false) return node;
    } catch (e) {
      console.warn('[AmaView] Falha num popup por coordenada:', e);
    }
  }
  return null;
}
